import { StoragePayload } from "../api/payload/seguridad/StoragePayload";

export class SecureStorageService {
  constructor() {}

  // ─── Utilidades privadas ───────────────────────────────

  private bufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToBuffer(base64: string): Uint8Array<ArrayBuffer> {
    const binary = atob(base64);
    const buffer = new ArrayBuffer(binary.length);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < binary.length; i++) {
      view[i] = binary.charCodeAt(i);
    }
    return view as Uint8Array<ArrayBuffer>;
  }

  private randomBytes(length: number): Uint8Array<ArrayBuffer> {
    const buffer = new ArrayBuffer(length);
    const view = new Uint8Array(buffer) as Uint8Array<ArrayBuffer>;
    crypto.getRandomValues(view);
    return view;
  }

  private getFingerprint(): string {
    return [
      navigator.userAgent,
      navigator.language,
      screen.colorDepth,
      `${screen.width}x${screen.height}`,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      window.location.origin,
    ].join("|");
  }

  private async hashKey(name: string): Promise<string> {
    const buffer = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(name + window.location.origin),
    );
    return this.bufferToBase64(buffer).substring(0, 16);
  }

  private async getOrCreateSalt(): Promise<Uint8Array<ArrayBuffer>> {
    const saltKey = await this.hashKey("salt");
    const existing = localStorage.getItem(saltKey);

    if (existing) {
      return this.base64ToBuffer(existing);
    }

    const salt = this.randomBytes(32);
    localStorage.setItem(saltKey, this.bufferToBase64(salt.buffer));
    return salt;
  }

  private async deriveKey(): Promise<CryptoKey> {
    const salt = await this.getOrCreateSalt();
    const fingerprint = this.getFingerprint();
    const raw = new TextEncoder().encode(fingerprint);

    const base = await crypto.subtle.importKey("raw", raw, "PBKDF2", false, [
      "deriveKey",
    ]);

    return crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: salt,
        iterations: 100_000,
        hash: "SHA-256",
      },
      base,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    );
  }

  private async _setItem(name: string, value: string): Promise<void> {
    const storageKey = await this.hashKey(name);
    const key = await this.deriveKey();
    const iv = this.randomBytes(12);

    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      new TextEncoder().encode(value),
    );

    const payload: StoragePayload = {
      iv: this.bufferToBase64(iv.buffer),
      data: this.bufferToBase64(encrypted),
    };

    localStorage.setItem(storageKey, btoa(JSON.stringify(payload)));
  }

  private async _getItem(name: string): Promise<string | null> {
    const storageKey = await this.hashKey(name);
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;

    try {
      const payload: StoragePayload = JSON.parse(atob(raw));
      const key = await this.deriveKey();
      const decrypted = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: this.base64ToBuffer(payload.iv) },
        key,
        this.base64ToBuffer(payload.data),
      );
      return new TextDecoder().decode(decrypted);
    } catch {
      localStorage.removeItem(storageKey);
      return null;
    }
  }

  // ─── Token de sesión ───────────────────────────────────

  async set(value: string): Promise<void> {
    return this._setItem("session", value);
  }

  async get(): Promise<string | null> {
    return this._getItem("session");
  }

  async remove(): Promise<void> {
    const sessionKey = await this.hashKey("session");
    const saltKey = await this.hashKey("salt");
    localStorage.removeItem(sessionKey);
    localStorage.removeItem(saltKey);
  }
}

// Instancia global
export const secureStorage = new SecureStorageService();
